import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { DatetimeService } from '../../../shared/services/datetime.service';

import { AuditApiService } from '../../audit/services/audit-api.service';
import { AuditLogResponse, PageResponse, UserTimelineResponse } from '../../audit/models/audit.model';
import { UserApiService } from '../services/user-api.service';
import { CrmRoleResponse, CrmUserResponse } from '../models/user.model';

type UserTab = 'info' | 'timeline';

/**
 * Detalle estándar de Usuario.
 *
 * <p>Antes el detalle vivía embebido en `user-list.component.html` como un bloque suelto
 * dentro de un `app-modal`: una sola columna de datos, sin pestañas y sin historial. Era el
 * único detalle del módulo que no seguía el patrón canónico del sistema (contactos, empresas,
 * workflows, segmentos y formularios usan todos un componente propio
 * `<entidad>-detail-modal/` con encabezado de avatar y pestañas Información + Línea de
 * tiempo), así que se extrajo aquí y se alineó con él.</p>
 *
 * <p>Igual que el resto de detalles: el pie solo ofrece "Editar" y "Cerrar" — las acciones de
 * estado (activar, desactivar, restablecer contraseña, eliminar datos) viven en la fila de la
 * lista, que es su sitio, y no se duplican.</p>
 */
@Component({
  selector: 'app-user-detail-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent, BadgeComponent, AuditTimelineComponent, HasPermissionDirective],
  templateUrl: './user-detail-modal.component.html'
})
export class UserDetailModalComponent implements OnChanges {
  @Input() isOpen = false;

  /**
   * Fila de la LISTA. Se usa para pintar el modal al instante y se sustituye por el detalle
   * completo en cuanto llega `GET /api/auth/users/{id}` (ver `detalle`): la búsqueda paginada
   * no siempre enriquece `businessName` ni trae los campos de perfil (teléfono, cargo, bio).
   */
  @Input() user: CrmUserResponse | null = null;

  /** Catálogo de roles del negocio, para resolver el nombre a partir de `idRol`. */
  @Input() roles: CrmRoleResponse[] = [];

  /** Nombre del negocio ya resuelto por el contenedor (la lista carga el catálogo). */
  @Input() businessName = '';

  /** Solo Super Admin edita usuarios; el contenedor ya lo sabe y lo pasa resuelto. */
  @Input() canEdit = false;

  @Output() close = new EventEmitter<void>();
  @Output() edit = new EventEmitter<CrmUserResponse>();

  activeTab: UserTab = 'info';

  readonly tabs: { id: UserTab; label: string }[] = [
    { id: 'info', label: 'Información' },
    { id: 'timeline', label: 'Línea de tiempo' }
  ];

  timelineLogs: AuditLogResponse[] = [];
  timelineLoading = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;
  /** Evita recargar el historial en cada ida y vuelta a la pestaña cuando no hay eventos. */
  timelineLoaded = false;

  /**
   * true = lo que se está mostrando viene del endpoint ANTIGUO porque este servidor no conoce
   * `/logs/entity/...`. Cambia el contenido de la pestaña (actividad del usuario en vez de
   * cambios sobre su cuenta), así que la plantilla lo advierte de forma visible.
   */
  timelineDegradado = false;

  detalle: CrmUserResponse | null = null;
  detalleLoading = false;

  /** Fuente de verdad para la plantilla: el detalle si ya llegó, si no la fila de la lista. */
  get vm(): CrmUserResponse | null {
    return this.detalle ?? this.user;
  }

  constructor(
    private auditApi: AuditApiService,
    private datetimeService: DatetimeService,
    private userApi: UserApiService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.activeTab = 'info';
      this.timelineLogs = [];
      this.timelineError = '';
      this.timelinePage = 0;
      this.timelineTotalPages = 0;
      this.timelineTotalElements = 0;
      this.timelineLoaded = false;
      this.timelineDegradado = false;
      this.cargarDetalle();
    }
  }

  /** Si falla, se conserva la fila de la lista: el modal degrada, no se rompe. */
  private cargarDetalle(): void {
    this.detalle = null;
    const id = this.user?.id;
    if (!id) return;
    this.detalleLoading = true;
    this.userApi.getById(id).subscribe({
      next: (full) => { this.detalle = full; this.detalleLoading = false; },
      error: () => { this.detalleLoading = false; }
    });
  }

  setTab(tab: UserTab): void {
    this.activeTab = tab;
    if (tab === 'timeline' && !this.timelineLoaded && !this.timelineLoading && this.user) {
      this.cargarTimeline(0);
    }
  }

  /**
   * El nombre de entidad va en minúscula ('user'), como el resto del sistema: la comparación
   * contra `audit_log.entity_name` distingue mayúsculas.
   *
   * <p>Se usa `getRecordTimeline` y no `getEntityTimeline` porque la ruta genérica está
   * ensombrecida para `user`: `/api/audit/logs/user/{id}/timeline` es el historial de lo que
   * ESE usuario hizo (incluidos sus logins), no el de los cambios sobre su cuenta, que es lo
   * que muestran los detalles del resto del sistema.</p>
   */
  cargarTimeline(page: number): void {
    const id = this.vm?.id;
    if (!id) return;
    this.timelineLoading = true;
    this.timelineError = '';
    this.timelineDegradado = false;
    this.timelinePage = page;
    this.auditApi.getRecordTimeline('user', String(id), { page, size: 15 }).subscribe({
      next: (res) => this.aplicarTimeline(res, page),
      error: (err) => {
        // 404 = ESTE servidor no conoce el alias /logs/entity/... (se añadió después de su
        // build). Es el único caso que justifica reintentar: un 403 es una decisión de
        // permisos y un 500 un fallo real, y taparlos con otra consulta escondería el problema.
        if (err?.status === 404) {
          this.cargarTimelineLegado(id, page);
          return;
        }
        this.fallarTimeline(err);
      }
    });
  }

  /**
   * Plan B para servidores que aún no tienen el alias: `/logs/user/{id}/timeline`.
   *
   * <p><strong>No devuelve lo mismo</strong>, y por eso la pantalla lo advierte en vez de
   * disimularlo: esta ruta lista lo que ESE usuario HIZO (sus operaciones sobre el CRM y sus
   * inicios de sesión), no los cambios sobre su cuenta, que es lo que muestra el resto de
   * detalles del sistema. Tampoco trae el detalle campo a campo ni el autor de cada cambio.
   * Presentarlo sin decirlo llevaría a leer "este usuario creó 12 contactos" como si fuera el
   * historial de su ficha.</p>
   */
  private cargarTimelineLegado(id: number, page: number): void {
    this.auditApi.getUserTimeline(id, { page, size: 15 }).subscribe({
      next: (res) => {
        this.timelineDegradado = true;
        this.aplicarTimeline(
          { ...res, content: (res.content ?? []).map((e) => this.legadoALog(e)) },
          page
        );
      },
      error: (err) => this.fallarTimeline(err, true)
    });
  }

  private aplicarTimeline(res: PageResponse<AuditLogResponse>, page: number): void {
    this.timelineLogs = res.content ?? [];
    this.timelinePage = res.page ?? page;
    this.timelineTotalPages = res.totalPages ?? 0;
    this.timelineTotalElements = res.totalElements ?? 0;
    this.timelineLoading = false;
    this.timelineLoaded = true;
  }

  private fallarTimeline(err: { status?: number } | null, traseIntentarLegado = false): void {
    this.timelineLogs = [];
    this.timelineDegradado = false;
    if (err?.status === 403) {
      this.timelineError = 'No tienes permiso para ver el historial de auditoría.';
    } else if (traseIntentarLegado && err?.status === 404) {
      // Ni el alias ni la ruta antigua: el servidor es más antiguo que ambas o la auditoría
      // no está publicada. Decirlo es más útil que un "Error al cargar" que invita a reintentar.
      this.timelineError = 'El historial no está disponible en esta versión del servidor.';
    } else {
      this.timelineError = 'Error al cargar la línea de tiempo.';
    }
    this.timelineLoading = false;
    // No se marca como cargada: al volver a la pestaña se reintenta.
  }

  /**
   * Adapta una entrada del endpoint antiguo a la forma que pinta `app-audit-timeline`.
   *
   * <p>Los campos que la ruta antigua NO tiene se dejan fuera a propósito en lugar de
   * inventarlos: `changesDisplay` va a `null` para que el componente no dibuje un diff vacío,
   * y `username`/`userFullName` quedan vacíos porque en esta vista el actor es siempre el
   * propio usuario del que se está viendo la ficha.</p>
   */
  private legadoALog(e: UserTimelineResponse): AuditLogResponse {
    return {
      id: e.id,
      entityName: e.entityName,
      idEntity: e.idEntity ?? '',
      moduleName: '',
      operationType: e.operationType,
      operationSubtype: e.operationSubtype,
      changesDisplay: null,
      idUser: this.vm?.id ?? 0,
      username: '',
      businessId: this.vm?.businessId ?? 0,
      timestamp: e.timestamp,
      description: e.description,
      severity: 'INFO',
      success: e.success ?? true
    };
  }

  // ── Presentación ──────────────────────────────────────────────────────────

  get displayName(): string {
    const u = this.vm;
    if (!u) return '';
    const nombre = `${u.fullname ?? ''} ${u.lastname ?? ''}`.trim();
    return nombre || u.mail || '—';
  }

  get initials(): string {
    const u = this.vm;
    if (!u) return '?';
    const f = (u.fullname ?? '').trim();
    const l = (u.lastname ?? '').trim();
    const iniciales = `${f.charAt(0)}${l.charAt(0)}`.trim().toUpperCase();
    return iniciales || (u.mail ?? '?').charAt(0).toUpperCase();
  }

  get roleName(): string {
    const u = this.vm;
    if (!u) return '—';
    const nombre = u.roleName?.trim();
    if (nombre) return nombre;
    return this.roles.find(r => r.id === u.idRol)?.name ?? `Rol ${u.idRol}`;
  }

  get statusLabel(): string {
    return this.vm?.isActive ? 'Activo' : 'Inactivo';
  }

  get statusColor(): 'success' | 'error' {
    return this.vm?.isActive ? 'success' : 'error';
  }

  /**
   * El detalle traído por id enriquece `businessName`; si aún no llegó se cae al nombre que
   * resolvió el contenedor con su catálogo de negocios.
   */
  get businessLabel(): string {
    return this.vm?.businessName?.trim() || this.businessName?.trim() || '—';
  }

  formatDate(value?: string | null): string {
    return value ? this.datetimeService.formatDateTime(value) : '—';
  }

  onClose(): void { this.close.emit(); }

  onEdit(): void {
    const u = this.vm;
    if (u) this.edit.emit(u);
  }
}
